import { useEffect, useRef, useState } from 'react';
import pageService from '../../services/summary/summary.service';
import flowsService from '../../services/flows/flows.service';
import UrlsUtil from '../../utils/url.util';
import { DateTime } from 'luxon';

type Persistence = {
  endDate: string,
  filter: string,
  flowFilter: string,
  flows: any[],
  history: any[],
  lastUpdated: string | undefined,
  rawData: any[],
  selectedSummary: any | undefined,
  startDate: string,
  stateFilter: any,
  stateFilterOptions: any[],
  summaries: any[],
  uniqueFlows: any[]
};

function Summary() {
  const nowSetup = DateTime.utc();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [persistence, setPersistence] = useState<Persistence>({
    endDate: nowSetup.toISODate()!,
    filter: '',
    flowFilter: '',
    flows: [],
    history: [],
    lastUpdated: 'loading -- please wait...',
    rawData: [],
    selectedSummary: undefined,
    startDate: nowSetup.minus({ days: 30 }).toISODate()!,
    stateFilter: undefined,
    stateFilterOptions: [],
    summaries: [],
    uniqueFlows: []
  });

  const ws = useRef(null);

  // const activateEntry = async (customerId: string, entityId: string, entityType: string) => {
  //   await pageService.activate(customerId, entityId, entityType)
  //     .then(async () => {
  //       await getData(persistence.filter, persistence.stateFilter)
  //         .then(() => setIsFetching(false));
  //     });
  // };

  const clearFilter = () => {
    filterData(undefined);
    const filter = document.getElementById('filter') as HTMLInputElement;
    filter.value = '';
  };

  const deactivateEntry = async (customerId: string, entityId: string, entityType: string, flowKey: string) => {
    await pageService.deactivate(customerId, entityId, entityType, flowKey)
      .then(async () => {
        const selectedSummary = persistence.selectedSummary;
        selectedSummary.isActive = false;
        setPersistenceProperty('selectedSummary', selectedSummary);
        await getData(persistence.filter, persistence.stateFilter, persistence.flowFilter, persistence.startDate, persistence.endDate)
          .finally(() => {
            setIsFetching(false);
          });
      });
  };

  const deleteEntry = async (customerId: string, entityId: string, entityType: string, flowKey: string, name: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`WARNING:\nTHIS OPERATION IS PERMANENT AND CANNOT BE REVERSED.\n\nAre you sure you want to delete the following entry?\n----------------------------------------------------------\n${name}`)) {
      await pageService.delete(customerId, entityId, entityType, flowKey)
        .then(async () => {
          await getData(persistence.filter, persistence.stateFilter, persistence.flowFilter, persistence.startDate, persistence.endDate)
            .then(() => setIsFetching(false));
        });
    }
  };

  const executeFilterData = async (endDateValue: string, startDateValue: string, filter?: string, filterState?: string, filterFlow?: string, toDisplay?: any[]): Promise<any[]> => {
    if (!toDisplay) {
      toDisplay = persistence.rawData;
    }

    const sdp = DateTime.fromISO(startDateValue);
    const edp = DateTime.fromISO(endDateValue);
    const startDate = DateTime.fromObject({ year: sdp.year, month: sdp.month, day: sdp.day, hour: 0, minute: 0, second: 0, millisecond: 0 });
    const endDate = DateTime.fromObject({ year: edp.year, month: edp.month, day: edp.day, hour: 23, minute: 59, second: 59, millisecond: 999 });

    toDisplay = toDisplay.filter((item: any) => {
      let created = DateTime.fromISO(item.created);
      return created > startDate && created < endDate;
    });

    if (filter) {
      const filterLowered = filter.toLowerCase();
      toDisplay = toDisplay.filter((item: any) => {
        return persistence.selectedSummary
          ? item.content?.toLowerCase().indexOf(filterLowered) > -1
          || item.customerId?.toLowerCase().indexOf(filterLowered) > -1
          || item.description?.toLowerCase().indexOf(filterLowered) > -1
          || item.entityType.toLowerCase().indexOf(filterLowered) > -1
          || item.entityId.toLowerCase().indexOf(filterLowered) > -1
          || item.flowKey.toLowerCase().indexOf(filterLowered) > -1
          || item.key.toLowerCase().indexOf(filterLowered) > -1
          || item.name?.toLowerCase().indexOf(filterLowered) > -1
          || item.currentStateDisplay?.toLowerCase().indexOf(filterLowered) > -1
          : item.customerId?.toLowerCase().indexOf(filterLowered) > -1
          || item.description?.toLowerCase().indexOf(filterLowered) > -1
          || item.entityType.toLowerCase().indexOf(filterLowered) > -1
          || item.entityId.toLowerCase().indexOf(filterLowered) > -1
          || item.key.toLowerCase().indexOf(filterLowered) > -1
          || item.name?.toLowerCase().indexOf(filterLowered) > -1
          || item.currentStateDisplay?.toLowerCase().indexOf(filterLowered) > -1;
      });
    }

    if (filterState) {
      toDisplay = toDisplay.filter((item: any) => {
        return item.currentState.toLowerCase() === filterState.toLowerCase();
      });
    }

    if (filterFlow) {
      const flowIdParts: string[] = filterFlow.toLowerCase().split(',');
      const flowKey = flowIdParts[0];
      const flowVersion = flowIdParts[1];

      toDisplay = toDisplay.filter((item: any) => {
        return item.flowKey.toLowerCase() === flowKey && item.flowVersion === flowVersion;
      });
    }

    return toDisplay;
  };

  const filterData = async (e: any) => {
    let value = e?.target?.value || '';
    setPersistence(current => ({ ...current, 'filter': value }));
    const toDisplay = await executeFilterData(persistence.endDate, persistence.startDate, value, persistence.stateFilter, persistence.flowFilter);
    setPersistenceProperty('summaries', toDisplay);
  };

  const forceRefresh = async () => {
    await getData(persistence.filter, persistence.stateFilter, persistence.flowFilter, persistence.startDate, persistence.endDate);
  };

  const getData = async (filter?: string, filterState?: string, filterFlow?: string, startDate?: string, endDate?: string) => {
    if (!isFetching) {
      setPersistenceProperty('lastUpdated', `querying data...`);
      setIsFetching(true);

      const sDate: string = startDate || persistence.startDate;
      const eDate: string = endDate || persistence.endDate;

      const promisesToKeep: Promise<any>[] = [
        flowsService.getAll(),
        pageService.getAll(sDate, eDate)
      ];

      await Promise.all(promisesToKeep)
        .then(async (responses: any[]) => {
          const now = new Date();

          let flows: any[] = [];
          let uniqueFlows: any[] = [];
          const flowsResponse = responses[0];
          if (flowsResponse.status === 200 && flowsResponse.data) {
            const flowVersions: { [key: string]: string[] } = {};
            flowsResponse.data.map((flow: any) => {
              if (!Object.keys(flowVersions).some(x => x === flow.key)) {
                return flowVersions[flow.key] = [flow.version];
              }
              else {
                if (flowVersions[flow.key].indexOf(flow.version) === -1)
                  return flowVersions[flow.key].push(flow.version);
              }
              return undefined;
            });

            for (let fvKey of Object.keys(flowVersions)) {
              const flowVersion = flowVersions[fvKey];
              if (flowVersion.length > 0) {
                flowVersion.sort((a: string, b: string) => {
                  if (a > b) return -1;
                  if (a < b) return 1;
                  return 0;
                });
              }
            }

            flows = flowsResponse.data.map((flow: any) => {
              return {
                entityType: flow.entityType,
                key: flow.key,
                name: flow.name,
                version: flow.version,
                isActive: flowVersions[flow.key]
                  ? flowVersions[flow.key][0] === flow.version
                  : false
              };
            });

            for (let fi = 0; fi < flows.length; fi++) {
              const flow = flows[fi];
              if (!uniqueFlows.some(x => x.key === flow.key && x.version === flow.version)) {
                uniqueFlows.push(flow);
              }
            }
          }

          const response = responses[1];

          if (response.status === 200 && response.data) {
            response.data.sort((a: any, b: any) => {
              var aDate = new Date(a.created);
              var bDate = new Date(b.created);
              if (aDate > bDate) return -1;
              if (aDate < bDate) return 1;
              return 0;
            });

            const eligibleStates: any[] = [];
            response.data.forEach((item: any, rdi: number) => {
              if (rdi === 0) {
                localStorage.setItem('currentFirstKey', item.key);
              }
              const option = {
                flowKey: item.flowKey,
                state: item.currentState,
                key: item.currentState,
                text: item.currentStateDisplay?.split(',')[0]
              };
              if (!eligibleStates.some(x => x.flowKey === option.flowKey && x.state === option.state)) {
                eligibleStates.push(option);
              }
            });

            eligibleStates.sort((a: any, b: any) => {
              if (a.text > b.text) return 1;
              if (a.text < b.text) return -1;
              return 0;
            });

            const toPersist = await executeFilterData(eDate, sDate, filter, filterState, filterFlow, response.data);

            setPersistence(current => ({
              ...current,
              'lastUpdated': `last update: ${now.getHours()}:${`0${now.getMinutes()}`.slice(-2)}:${`0${now.getSeconds()}`.slice(-2)}`,
              'flows': flows,
              'rawData': response.data,
              'stateFilterOptions': eligibleStates,
              'summaries': toPersist,
              'uniqueFlows': uniqueFlows
            }));
          }
        })
        .finally(() => setIsFetching(false));
    }
  };

  const getAllDataFor = async (customerId: string, entityId: string, entityType: string, name: string) => {
    setPersistenceProperty('lastUpdated', `querying history for ${name}...`);
    setIsFetching(true);
    await pageService.getAllFor(customerId, entityId, entityType)
      .then(async response => {
        const now = new Date();
        if (response.status === 200) {
          setPersistenceProperty('lastUpdated', `last update: ${now.getHours()}:${`0${now.getMinutes()}`.slice(-2)}:${`0${now.getSeconds()}`.slice(-2)}`);

          response.data.sort((a: any, b: any) => {
            var aDate = new Date(a.created);
            var bDate = new Date(b.created);
            if (aDate > bDate) return -1;
            if (aDate < bDate) return 1;
            return 0;
          });

          if (response.data.length > 0) {
            setPersistenceProperty('history', response.data);

          }
          else {
            await getEntireDataFor(customerId, entityId, entityType, name);
          }
        }
      })
      .finally(() => setIsFetching(false));
  };

  const getEntireDataFor = async (customerId: string, entityId: string, entityType: string, name: string) => {
    setPersistenceProperty('lastUpdated', `querying history for ${name}...`);
    setIsFetching(true);
    await pageService.getEntireFor(customerId, entityId, entityType)
      .then(response => {
        const now = new Date();
        if (response.status === 200) {
          setPersistenceProperty('lastUpdated', `last update: ${now.getHours()}:${`0${now.getMinutes()}`.slice(-2)}:${`0${now.getSeconds()}`.slice(-2)}`);

          response.data.sort((a: any, b: any) => {
            var aDate = new Date(a.created);
            var bDate = new Date(b.created);
            if (aDate > bDate) return -1;
            if (aDate < bDate) return 1;
            return 0;
          });

          setPersistenceProperty('history', response.data);
        }
      });
  };

  const handleChange = (e: any, name: string) => {
    let value = e.target?.value || e.value || '';
    setPersistence(v => { return { ...v, [name]: value } });
    setTimeout(async () => {
      const endDate = name === 'endDate' ? value : persistence.endDate;
      const startDate = name === 'startDate' ? value : persistence.startDate;
      const textFilter = persistence.filter;
      const stateFilter = name === 'stateFilter' ? value : persistence.stateFilter;
      const flowFilter = name === 'flowFilter' ? value : persistence.flowFilter;
      if (name.toLowerCase().endsWith('date')) {
        await getData(textFilter, stateFilter, flowFilter, startDate, endDate);
      }
      else {
        await executeFilterData(endDate, startDate, textFilter, stateFilter, flowFilter)
          .then((result: any) => {
            setPersistenceProperty('summaries', result);
          });
      }
    }, 250);
  };

  const handleDateChange = (e: any, name: string) => {
    let value = e.target?.value || e.value || '';

    const startDate = DateTime.fromISO(name === 'startDate' ? value : persistence.startDate);
    const endDate = DateTime.fromISO(name === 'endDate' ? value : persistence.endDate);
    const canFilter: boolean = startDate <= endDate;

    if (!canFilter) {
      setPersistence(v => { return { ...v, [name]: value } });
      alert('The End Date must be later or equal to the Start Date.');
      forceRefresh();
      return;
    }
    else {
      handleChange(e, name);
    }
  };

  const handleKeyUp = (e: any) => {
    const key = (e.key || e.keyCode).toString().toLowerCase();
    if ((key === '8' || key === 'backspace') && persistence.filter.length === 0) {
      filterData(undefined);
    }
  };

  const highlightElement = (pattern: RegExp, prop: any): string => {
    return persistence.filter
      ? prop?.replace(pattern, `<span class='filter-highlighted'>$&</span>`) || prop
      : prop;
  }

  const selectEntry = async (summary: any) => {
    setPersistenceProperty('history', []);

    const value = persistence.selectedSummary?.key === summary.key
      ? undefined
      : summary;
    setPersistenceProperty('selectedSummary', value);

    if (value) {
      localStorage.setItem('selectedSummary', JSON.stringify(value));
    } else {
      localStorage.removeItem('selectedSummary');
    }

    await getAllDataFor(summary.customerId, summary.entityId, summary.entityType, summary.name);

    const filter = document.getElementById('filter');
    if (filter) {
      // @ts-ignore
      filter.value = persistence.filter;
    }
  }

  const setPersistenceProperty = (name: string, value: any) => {
    const isSummaries = name === 'summaries';
    setPersistence(current => {
      const updated = { ...current, [name]: value };
      if (isSummaries && updated.selectedSummary) {
        const summary = updated.summaries.find((x: any) => x.customerId === updated.selectedSummary.customerId && x.entityId === updated.selectedSummary.entityId && x.entityType === updated.selectedSummary.entityType);
        if (summary) {
          updated.selectedSummary = summary;
        }
        else {
          updated.selectedSummary.isActive = false;
        }
      }
      return updated;
    });
  };

  useEffect(() => {

    /*
    
        // // @ts-ignore
        // ws.current = new WebSocket(`${process.env.REACT_APP_WSBASEURL}/ws`);
    
    
          let apiBaseUrl = `${process.env.REACT_APP_APIBASEURL}`;
          
          if(!window.location.hostname.includes('localhost')){
            apiBaseUrl = `https://${window.location.hostname}`;
          }
          
          console.log(apiBaseUrl);
          
          const ws = new WebSocket(`wss://${apiBaseUrl}}/ws`);
    */

    // @ts-ignore
    ws.current = new WebSocket(UrlsUtil.determineWebSocketUrl(window));

    // @ts-ignore
    ws.current.onopen = (event) => {
      console.log('summary: web socket opened.', event);
    };

    // @ts-ignore
    ws.current.onclose = (cls) => {
      console.log('summary: ws closed', cls);
    };

    // @ts-ignore
    ws.current.onerror = (err) => {
      console.log('summary: ws Error', err);
    };

    const wsCurrent = ws.current;
    return () => {
      // @ts-ignore
      wsCurrent?.close();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isInitialized) {
      const tokenCheck = localStorage.getItem('token');
      if (!tokenCheck) {
        window.location.href = "/";
      }

      console.log('initializing...');
      setIsInitialized(true);

      localStorage.removeItem('currentFirstKey');
      localStorage.removeItem('selectedSummary');

      // @ts-ignore
      ws.current.onmessage = async (msg) => {
        // console.log('summary: incoming message', msg);

        setTimeout(async () => {
          const selectedSummaryExists = localStorage.getItem('selectedSummary');
          await getData(persistence.filter, persistence.stateFilter, persistence.flowFilter, persistence.startDate, persistence.endDate)
            .then(async () => {
              if (selectedSummaryExists) {
                var summary = JSON.parse(selectedSummaryExists);
                setPersistenceProperty('lastUpdated', `updating history for ${summary.name}...`);
                setIsFetching(true);
                await pageService.getAllFor(summary.customerId, summary.entityId, summary.entityType)
                  .then(response => {
                    if (response.status === 200) {

                      response.data.sort((a: any, b: any) => {
                        var aDate = new Date(a.created);
                        var bDate = new Date(b.created);
                        if (aDate > bDate) return -1;
                        if (aDate < bDate) return 1;
                        return 0;
                      });

                      const currentFirstKey: any = localStorage.getItem('currentFirstKey') || undefined;
                      if (currentFirstKey) {
                        const now = new Date();

                        setPersistence(current => ({
                          ...current,
                          'lastUpdated': `last update: ${now.getHours()}:${`0${now.getMinutes()}`.slice(-2)}:${`0${now.getSeconds()}`.slice(-2)}`,
                          'history': response.data
                        }));

                        for (let i = 0; i < response.data.length; i++) {
                          var check = response.data[i];
                          const btn = document.getElementById(`btn_${check.key}`);
                          if (btn && currentFirstKey === check.key) {
                            btn.click();
                            break;
                          }
                        }
                      }
                    }
                  })
                  .finally(() => setIsFetching(false));
              }
            });
        }, 1100);
      };

      getData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, persistence]);

  return (
    <div className='data-display'>
      <div className="row" style={{ width: '100%', textAlign: 'left', marginBottom: '0.5em' }}>
        <div className='col-12'>
          <h1>Summaries</h1>
          <div>
            Showing {persistence.summaries?.length || 0} of {persistence.rawData?.length || 0} &bull; <span style={{ marginTop: '0em' }}>{persistence.lastUpdated}</span>
          </div>
        </div>
      </div>
      {
        isInitialized && persistence.lastUpdated && (persistence.lastUpdated.startsWith('last update: ') || persistence.lastUpdated.startsWith('querying data') || persistence.lastUpdated.startsWith('querying history')) &&
        <div className="row" style={{ width: '100%', textAlign: 'left', marginBottom: '0.5em', verticalAlign: 'middle' }}>
          <div className='col-12'>
            <table>
              <tbody>
                <tr>
                  <td className='header-filter'>
                    {
                      isInitialized &&
                      <button className='btn btn-success header-refresh' onClick={() => forceRefresh()}>Force Refresh</button>
                    }
                  </td>
                  <td className='header-filter'>
                    Filter by Keyword: <input type="text" id="filter" style={{ height: '38px' }} onInput={(e) => filterData(e)} onKeyUp={(e) => handleKeyUp(e)} />
                    {
                      persistence.filter &&
                      <span><button className='btn btn-link filter-clear' onClick={() => clearFilter()}>x</button></span>
                    }
                  </td>
                  <td className='header-filter'>
                    Date Range -
                    <span style={{ paddingLeft: '0.25em', paddingRight: '0.5em' }}>Start: <input type="date" defaultValue={persistence.startDate} onChange={(e) => handleDateChange(e, 'startDate')} /></span>
                    <span >End: <input type="date" defaultValue={persistence.endDate} onChange={(e) => handleDateChange(e, 'endDate')} /></span>
                  </td>
                </tr>                
                <tr>
                  <td></td>
                  <td className='header-filter'>
                    {
                      persistence.uniqueFlows?.length > 0 &&
                      <>
                        Filter by Flow:
                        <select onChange={(e) => handleChange(e, 'flowFilter')} style={{ marginLeft: '0.25em', height: '38px' }}>
                          <option value=''>-- show all flows --</option>
                          {
                            persistence.uniqueFlows.map((option: any, oi: number) => {
                              return <option key={oi} value={`${option.key},${option.version}`}>{option.name}, v{option.version}</option>
                            })
                          }
                        </select>
                      </>
                    }
                  </td>
                  <td className='header-filter'>
                    {
                      persistence.stateFilterOptions?.length > 0 &&
                      <>
                        Filter by State:
                        <select onChange={(e) => handleChange(e, 'stateFilter')} style={{ marginLeft: '0.25em', height: '38px' }}>
                          <option value=''>-- show all states --</option>
                          {
                            persistence.stateFilterOptions.map((option: any, oi: number) => {
                              return <option key={oi} value={option.key}>{option.text}</option>
                            })
                          }
                        </select>
                      </>
                    }
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      }
      <table className='table table-striped'>
        <tbody>
          {
            (persistence.filter || persistence.flowFilter || persistence.stateFilter) && persistence.summaries.length === 0
              ? <tr><td><strong>No matching records to display</strong></td></tr>
              : persistence.summaries.map((summary: any, i) => {
                var summaryFlow = JSON.parse(summary.content)?.Flow;
                const flow = persistence.flows.find(x => x.key === summary.flowKey && x.isActive === true);

                const pattern = new RegExp(persistence.filter, 'ig');
                const updatedName = highlightElement(pattern, summary.name);
                const updatedCurrentStateDisplay = highlightElement(pattern, summary.currentStateDisplay);
                const updatedCustomerId = highlightElement(pattern, summary.customerId);
                const updatedDescription = highlightElement(pattern, summary.description);
                const updatedEntityId = highlightElement(pattern, summary.entityId);
                const updatedEntityType = highlightElement(pattern, summary.entityType);
                const updatedKey = highlightElement(pattern, summary.key);
                const updatedExtras = highlightElement(pattern, summary.extras || 'None');
                const flowDisplay = summaryFlow
                  ? summaryFlow
                  : flow
                    ? `${flow.name} (${flow.entityType}, v ${flow.version})`
                    : 'UNKNOWN!';

                return <tr key={i}>
                  <td>
                    <div className='row'>
                      <h2 key={`name_${i}`} dangerouslySetInnerHTML={{ __html: updatedName }}></h2>
                      <div className='col-5 row-left-panel'>
                        <p>
                          <strong>Description:</strong> <span dangerouslySetInnerHTML={{ __html: updatedDescription }}></span><br />
                          <strong>Flow:</strong> {flowDisplay} -- <span style={{ color: summary.isActive ? 'green' : 'red', fontWeight: 'bold' }}>{summary.isActive ? 'ACTIVE' : 'DEACTIVATED'}</span><br />
                          <strong>Current State:</strong> <span dangerouslySetInnerHTML={{ __html: updatedCurrentStateDisplay }}></span><br />
                          <strong>Entity Type:</strong> <span dangerouslySetInnerHTML={{ __html: updatedEntityType }}></span><br />
                          <strong>Entity Id:</strong> <span dangerouslySetInnerHTML={{ __html: updatedEntityId }}></span><br />
                          <strong>Created:</strong> {new Date(summary.created).toString()}<br />
                          <strong>Customer ID:</strong> <span dangerouslySetInnerHTML={{ __html: updatedCustomerId }}></span><br />
                          <strong>Key:</strong> <span dangerouslySetInnerHTML={{ __html: updatedKey }}></span><br />
                          <strong>Extras: </strong><span dangerouslySetInnerHTML={{ __html: updatedExtras }}></span>
                        </p>
                      </div>

                      <div className='col-1 center'>
                        <strong>Actions:</strong><br />
                        <button className='btn btn-primary' id={`btn_${summary.key}`} onClick={() => selectEntry(summary)}>{persistence.selectedSummary?.key !== summary.key ? 'View' : 'Hide'}</button><br />
                        {
                          persistence.selectedSummary?.key === summary.key &&
                          <>
                            {
                              persistence.selectedSummary.isActive &&
                              <>
                                <button className='btn btn-warning' style={{ marginTop: '0.2em' }} onClick={() => deactivateEntry(persistence.selectedSummary.customerId, persistence.selectedSummary.entityId, persistence.selectedSummary.entityType, persistence.selectedSummary.flowKey)}>Deactivate</button><br />
                              </>
                            }

                            {/* {
                              !persistence.selectedSummary.isActive &&
                              <>
                                <button className='btn btn-info' style={{ marginTop: '0.2em' }} onClick={() => activateEntry(persistence.selectedSummary.customerId, persistence.selectedSummary.entityId, persistence.selectedSummary.entityType)}>Activate</button><br />
                              </>
                            } */}

                            <button className='btn btn-danger' style={{ marginTop: '0.2em' }} onClick={() => deleteEntry(persistence.selectedSummary.customerId, persistence.selectedSummary.entityId, persistence.selectedSummary.entityType, persistence.selectedSummary.flowKey, persistence.selectedSummary.name)}>delete</button>
                          </>
                        }
                      </div>

                      <div className='col-6 row-right-panel'>
                        {
                          persistence.selectedSummary?.key === summary.key &&
                          <div>
                            <h2>{persistence.selectedSummary.name}:</h2>
                            <div>
                              <h5>Last Received:</h5>
                              <strong>Created: </strong> {new Date(persistence.selectedSummary.created).toString()}<br />
                              <strong>Event:</strong> {JSON.parse(persistence.selectedSummary.content).Event}<br />
                              <strong>Event Flow:</strong> {JSON.parse(persistence.selectedSummary.content).Flow}<br />
                              <strong>Event Content:</strong> {JSON.parse(persistence.selectedSummary.content).IncomingMessage}<br />
                            </div>
                            {
                              persistence.history &&
                              <div className='history'>
                                <h3>Previous Events:</h3>
                                {persistence.history.length > 0
                                  ? <ol reversed>
                                    {
                                      persistence.history.map((item: any, iti: number) => {
                                        if (persistence.selectedSummary.key === item.key) {
                                          return <span key={`skipped_${iti}`} ></span>;
                                        }
                                        let cEvent: string = '';
                                        let cEContent: string = 'None';
                                        if (item.content) {
                                          const ec = JSON.parse(item.content);
                                          cEvent = ec.Event;
                                          cEContent = ec.IncomingMessage;
                                        }


                                        return <li key={iti} className='history-item'>
                                          <div>
                                            <h4>{item.name}</h4>
                                            <strong>Created:</strong> {new Date(item.created).toString()}<br />
                                            <strong>State:</strong> {item.currentStateDisplay}<br />
                                            <strong>Event:</strong> {cEvent}<br />
                                            <strong>Description:</strong> {item.description}<br />
                                            <strong>Key:</strong> <span>{item.key}</span><br />
                                            <strong>Extras: </strong>{item.extras || 'None'}<br />

                                            <strong>Event Content:</strong> {cEContent || 'None'}<br />
                                          </div>
                                        </li>
                                      })
                                    }
                                  </ol>
                                  : <strong>none</strong>}
                              </div>
                            }
                          </div>
                        }
                      </div>
                    </div>
                  </td>
                </tr>
              })
          }
        </tbody>
      </table>
    </div>
  );
}

export default Summary;